/**
 * @fileoverview The metrics rail on the Lessons page.
 *
 * Every number here is computed from the SAME filtered array the list renders,
 * so the rail always describes exactly the rows on screen — change the period
 * and the headline, the charts and the list move together. Nothing here
 * fetches; `lib/lessonHistory` does the arithmetic.
 *
 * Chart discipline (see the data-viz rules): one hue per chart because these
 * are magnitude questions, not identity ones — the second hue appears only
 * where a row genuinely IS a different kind of thing (a class), and the name
 * beside it carries the identity regardless. Bars are thin, capped, rounded at
 * the data end and square at the baseline; gridlines are hairline and
 * recessive; labels wear text tokens, never the series colour.
 */

import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Card, Eyebrow, Icon } from "@/components/studio/primitives";
import { bucketByStudent, bucketOverTime, computeMetrics, periodLabel } from "@/lib/lessonHistory";

const ACCENT = "var(--accent-yellow)";
const COOL = "var(--accent-cool)";

/** Hours to one decimal below 100, whole above — "128.4 hrs" is false precision. */
function formatHours(hours) {
  if (hours >= 100) return Math.round(hours).toLocaleString();
  return (Math.round(hours * 10) / 10).toString();
}

const formatMinutes = (seconds) => `${Math.round(seconds / 60)} min`;

/**
 * A headline number. Deliberately not the shared StatCard: that one is a
 * 40px hero built for a 2-across dashboard grid, and four of them stacked in
 * a 320px rail would be the loudest thing on a page whose subject is the list.
 */
function Metric({ value, label, sub, tone = "default", onPress, testId }) {
  const toneClass = {
    default: "text-[var(--text-primary)]",
    accent: "text-[var(--accent-yellow-text)]",
    muted: "text-[var(--text-secondary)]",
  }[tone];

  const inner = (
    <>
      <div className={`font-mono text-[26px] font-light leading-none tracking-[-0.01em] ${toneClass}`}>{value}</div>
      <div className="mt-2 font-sans text-[13px] font-medium text-[var(--text-primary)]">{label}</div>
      {sub && <div className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">{sub}</div>}
    </>
  );

  if (!onPress) {
    return (
      <div className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4" data-testid={testId}>
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onPress}
      data-testid={testId}
      className="rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4 text-left transition-colors hover:bg-[var(--studio-overlay)]"
    >
      {inner}
    </button>
  );
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-[10px] border border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] px-3 py-2 shadow-[var(--studio-shadow)]">
      <div className="font-sans text-[12px] font-medium text-[var(--text-primary)]">{row.name || label}</div>
      <div className="mt-0.5 font-mono text-[12px] text-[var(--text-secondary)]">
        {row.count} {unit}
        {row.seconds ? ` · ${formatMinutes(row.seconds / Math.max(1, row.count))} avg` : ""}
      </div>
    </div>
  );
}

/** Section shell so the two charts share one heading treatment. */
function Panel({ title, caption, children, testId }) {
  return (
    <Card className="p-0" style={{ padding: 18 }} data-testid={testId}>
      <div className="mb-1 font-sans text-[13px] font-semibold text-[var(--text-primary)]">{title}</div>
      {caption && <div className="mb-3 font-sans text-[12px] text-[var(--text-tertiary)]">{caption}</div>}
      {children}
    </Card>
  );
}

/**
 * "Lessons over time" — a column chart, because the reader's job is comparing
 * magnitude across an ordered axis. Bucket width follows the period (weeks for
 * a month, months for a year), so the bar count stays readable either way.
 */
function OverTime({ filtered, period }) {
  const { grain, buckets } = useMemo(() => bucketOverTime(filtered, period), [filtered, period]);
  const busiest = useMemo(() => buckets.reduce((max, b) => Math.max(max, b.count), 0), [buckets]);

  // The axis is always driven explicitly. Left to itself recharts derives
  // ticks from the raw max and only THEN drops decimals, which gives
  // 0,1,1,1,2 for a small month (the same label four times) and 0,9,18,26,35
  // for a big one (ticks nobody reads off a chart). So: round the top up to a
  // clean step and lay four intervals on it.
  const { axisMax, ticks } = useMemo(() => {
    const top = Math.max(1, busiest);
    if (top <= 4) return { axisMax: top, ticks: Array.from({ length: top + 1 }, (_, i) => i) };
    // A "nice" step — 1, 2 or 5 times a power of ten, the smallest that keeps
    // the axis to about four intervals — and then a ceiling rounded UP to that
    // step rather than a fixed four of them. Both halves matter: snapping the
    // step to a multiple of 5 drew a six-lesson week against a ceiling of 20,
    // and forcing exactly four intervals put a nine-lesson week against 20 as
    // well. Bars that fill a third of their panel read as a quiet studio when
    // it was a busy one.
    const rough = top / 4;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    const step = [1, 2, 5, 10].map((m) => m * magnitude).find((c) => c >= rough) || magnitude * 10;
    const max = Math.ceil(top / step) * step;
    const out = [];
    for (let t = 0; t <= max; t += step) out.push(t);
    return { axisMax: max, ticks: out };
  }, [busiest]);

  // At most ~6 x-labels, whatever the bucket count.
  const labelStride = Math.max(0, Math.ceil(buckets.length / 6) - 1);

  if (!buckets.length) return null;

  const grainWord = { week: "week", month: "month", quarter: "quarter" }[grain];

  return (
    <Panel
      title="Lessons over time"
      caption={`By ${grainWord} · busiest ${grainWord}: ${busiest}`}
      testId="metric-over-time"
    >
      <div style={{ height: 132 }}>
        <ResponsiveContainer width="100%" height="100%">
          {/* No negative left margin: it used to pull the plot over the axis
              and clip two-digit tick labels ("35" rendered as "5"). The YAxis
              `width` is what reserves the gutter. */}
          <BarChart data={buckets} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barCategoryGap="26%">
            <CartesianGrid vertical={false} stroke="var(--studio-divider)" strokeWidth={1} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              /* An explicit stride, not "preserveStartEnd": recharts sizes that
                 one from an estimate and let "Jan" and "Feb" overprint each
                 other at 13 monthly buckets in a 300px rail. */
              interval={labelStride}
              minTickGap={14}
              tick={{ fill: "var(--text-quaternary)", fontSize: 10, fontFamily: "var(--font-mono)" }}
            />
            <YAxis
              allowDecimals={false}
              ticks={ticks}
              domain={[0, axisMax]}
              width={26}
              tickLine={false}
              axisLine={false}
              tick={{ fill: "var(--text-quaternary)", fontSize: 10, fontFamily: "var(--font-mono)" }}
            />
            <Tooltip cursor={{ fill: "var(--studio-tint)" }} content={<ChartTooltip unit="lessons" />} />
            {/* 4px rounded data-end, square at the baseline; capped so a
                sparse period doesn't render one enormous slab. */}
            <Bar dataKey="count" fill={ACCENT} radius={[4, 4, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

/**
 * "Who you taught" — a bar list rather than a charting-library horizontal bar:
 * the names ARE the axis, and a CSS row gives them the full width without the
 * truncation a fixed category axis forces.
 */
function ByStudent({ filtered, nameFor }) {
  const rows = useMemo(() => bucketByStudent(filtered, nameFor), [filtered, nameFor]);
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  if (!rows.length) return null;

  return (
    <Panel title="Who you taught" caption={`${rows.filter((r) => !r.isOther).length} shown, busiest first`} testId="metric-by-student">
      <div className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <div key={row.key} data-testid={`by-student-${row.key}`}>
            <div className="mb-1 flex items-baseline justify-between gap-2">
              <span
                className={`truncate font-sans text-[12px] ${row.isOther ? "text-[var(--text-tertiary)]" : "text-[var(--text-secondary)]"}`}
              >
                {row.name}
              </span>
              <span className="shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">{row.count}</span>
            </div>
            <div className="h-[6px] w-full overflow-hidden rounded-full bg-[var(--studio-interactive)]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(4, (row.count / max) * 100)}%`,
                  background: row.isOther ? "var(--text-quaternary)" : row.isClass ? COOL : ACCENT,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function LessonHistoryMetrics({ filtered, allLessons, period, nameFor, onShowUnsent, unsentActive, loading }) {
  const metrics = useMemo(
    () => computeMetrics(filtered, { period, allLessons }),
    [filtered, allLessons, period]
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-3" data-testid="lesson-metrics-loading">
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-[96px] rounded-[14px] bg-[var(--studio-raised)] skeleton" />)}
        </div>
        <div className="h-[190px] rounded-[14px] bg-[var(--studio-raised)] skeleton" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="lesson-metrics">
      <Eyebrow>{periodLabel(period)}</Eyebrow>

      <div className="grid grid-cols-2 gap-3">
        <Metric
          testId="metric-lessons"
          value={metrics.count.toLocaleString()}
          label="Lessons"
          sub={metrics.classCount ? `${metrics.classCount} with a class` : "All individual"}
        />
        <Metric
          testId="metric-hours"
          value={formatHours(metrics.hours)}
          label="Teaching hours"
          sub={metrics.count ? `${formatMinutes(metrics.avgSeconds)} average` : "—"}
        />
        <Metric
          testId="metric-per-week"
          value={metrics.perWeek >= 10 ? Math.round(metrics.perWeek) : Math.round(metrics.perWeek * 10) / 10}
          label="Lessons a week"
          sub={`${metrics.studentCount} students & classes`}
        />
        {/* The only metric that is a to-do rather than a fact, so it is the
            only one that is clickable and the only one that goes accent when
            it isn't zero — an unsent summary is work the family never got. */}
        <Metric
          testId="metric-unsent"
          value={metrics.unsent}
          label="Not sent"
          sub={metrics.unsent ? (unsentActive ? "Showing these" : "Tap to review") : `${metrics.sharedPct}% shared`}
          tone={metrics.unsent ? "accent" : "muted"}
          onPress={metrics.unsent ? onShowUnsent : undefined}
        />
      </div>

      {metrics.count === 0 ? (
        <Card style={{ padding: 18 }}>
          <div className="flex items-center gap-2 text-[var(--text-tertiary)]">
            <Icon name="chart" size={16} />
            <span className="font-sans text-[13px]">No lessons in this period</span>
          </div>
        </Card>
      ) : (
        <>
          <OverTime filtered={filtered} period={period} />
          <ByStudent filtered={filtered} nameFor={nameFor} />
        </>
      )}
    </div>
  );
}
